import {Injectable} from '@angular/core';
import {
    OpenTestMailEvent,
    ViewTemplateId,
    WorkbenchPlugin,
    WorkbenchPluginId,
    WorkbenchViewConfig, WorkbenchViewKind,
    WorkbenchViewTemplateConfig
} from '../../shared/types';
import { WorkbenchLayoutManager } from '../../shared/services/workbench-layout-manager.service';
import { WorkbenchCommandRegistry } from '../../shared/services/workbench-command-registry.service';
import {Observable, of} from 'rxjs';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import {MailBrowserComponent} from './views/mail-browser.component';
import {CENTER_VIEW_CONTAINER_CONFIG, CENTER_VIEW_CONTAINER_ID} from '../../shared/components/viewcontainers/viewcontainers';
import {MailViewConfig, MailCenterViewComponent} from './views/mail-center-view.component';
import _ from 'lodash';
import {ArtifactId} from '@webmate-web-core/shared/types/ids';
import {InspectorService} from '../../shared/services/inspector.service';

@Injectable()
export class MailPlugin extends WorkbenchPlugin {
    id: WorkbenchPluginId = 'mail';
    views: WorkbenchViewKind[];

    static readonly ViewContainerId: string = 'mail.viewcontainer';
    static readonly MailViewId: string = 'mail-browser.view.mail';
    static readonly ViewTemplateId: ViewTemplateId = 'mail.mail-view-template';

    constructor(readonly layoutManager: WorkbenchLayoutManager,
                private readonly commandRegistry: WorkbenchCommandRegistry,
                private inspectorService: InspectorService){
        super(layoutManager);

        const consoleViewTemplate: WorkbenchViewTemplateConfig<MailViewConfig> = {
            templateId: MailPlugin.ViewTemplateId,
            component: MailCenterViewComponent,
            icon: {
                type: 'faIcon',
                instance: faEnvelope
            },

            defaultViewContainer: CENTER_VIEW_CONTAINER_ID,

            init: (initPayload: {mailId: ArtifactId}) => {
                let config = _.cloneDeep(consoleViewTemplate) as WorkbenchViewConfig<MailViewConfig>;
                config.id = 'screenshot.view.' + (initPayload.mailId);
                config.fromTemplate = MailPlugin.ViewTemplateId;
                config.name = 'Mail View';
                config.payload = {
                    data: initPayload
                };
                return config as WorkbenchViewConfig<MailViewConfig>;
            }
        };

        this.views = [
            {
                id: MailPlugin.MailViewId,
                component: MailBrowserComponent,
                name: 'Mail Browser',
                defaultViewContainer: MailPlugin.ViewContainerId,
                expandedByDefault: true,
            },
            consoleViewTemplate
        ];
    }

    onWorkbenchDestroy() {}

    onWorkbenchInit(): Observable<void> {
        this.layoutManager.registerViewContainer({
            type: 'NormalViewContainer',
            id: MailPlugin.ViewContainerId,
            name: 'Mail',
            icon: {
                type: 'faIcon',
                instance: faEnvelope
            },
            allowedPanels: ['left-top'],
            defaultPanel: 'left-top',
            displayMode: 'standalone',
            toolbarCommandId: 'mail:toggleViewContainer'
        });

        this.layoutManager.registerViewContainer(CENTER_VIEW_CONTAINER_CONFIG);

        this.commandRegistry.registerCommand(
            'mail:toggleViewContainer',
            {
                name: 'Mail',
                icon: {
                    type: 'faIcon',
                    instance: faEnvelope,
                },
                description: 'Toggle Mail',
                statusBindings: {
                    'active': {
                        'type': 'store',
                        'storeKey': `viewcontainers.${MailPlugin.ViewContainerId}.status.visible`,
                    }
                }
            },
            () => {
                this.layoutManager.toggleViewContainer(MailPlugin.ViewContainerId, true);
            }
        );

        this.commandRegistry.registerCommand(
            'mail:openMail',
            {
                name: "Open specific mail in the Center View"
            },
            (params: any) => {
                const mailId = params.mailId;
                this.layoutManager.instantiateAndShowViewTemplate(MailPlugin.ViewTemplateId, {mailId: mailId});
            }
        );

        this.commandRegistry.registerCommand(
            "mail:openMailTools",
            {
                name: 'Open specific Tools for Test Mail',
            },
            (params: any) => {
                this.inspectorService.changeItem('TestMail');
                const event: OpenTestMailEvent = {type: 'OpenTestMail', mailArtifatId: params.artifactId};
                setTimeout(() => this.inspectorService.raiseInspectorEvent(event));
            }
        )

        return of(undefined);
    }
}
